import type { Bbox2 } from "../geo/bbox";

/** Freehand pen stroke or a text note, always in world (CRS) coordinates. */
export type Annotation =
  | { type: "stroke"; points: Array<{ x: number; y: number }>; color: string; widthWorld: number }
  | { type: "text"; x: number; y: number; text: string; color: string; sizeWorld: number };

/**
 * Draws annotations onto any target extent/pixel-size — screen preview or export canvas — so
 * a stroke drawn on screen keeps the same real-world thickness and position when it lands on
 * the printed page, whatever DPI that page renders at.
 */
export function drawAnnotations(
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  extent: Bbox2,
  pixelWidth: number,
  pixelHeight: number,
): void {
  const pxPerWorldX = pixelWidth / (extent.maxX - extent.minX);
  const pxPerWorldY = pixelHeight / (extent.maxY - extent.minY);
  const toPx = (x: number, y: number) => ({
    x: (x - extent.minX) * pxPerWorldX,
    y: (extent.maxY - y) * pxPerWorldY,
  });

  for (const a of annotations) {
    if (a.type === "stroke") {
      if (a.points.length === 0) continue;
      // A soft, slightly translucent brush rather than a hard vector line — this is meant to
      // read as painting a mark over the plan, not drafting a precise line on it.
      ctx.save();
      ctx.globalAlpha = 0.85;
      ctx.strokeStyle = a.color;
      ctx.lineWidth = Math.max(0.5, a.widthWorld * pxPerWorldX);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      a.points.forEach((p, i) => {
        const { x, y } = toPx(p.x, p.y);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.restore();
    } else {
      drawTextNote(ctx, a, toPx, pxPerWorldY);
    }
  }
}

/**
 * A text note as a solid label: a filled box in the chosen colour with the text on top, in
 * whichever of black/white reads better against it — a colour picked to stand out against
 * imagery is not necessarily one text is legible in, so the note has to supply its own
 * contrast rather than relying on whatever happens to be underneath it.
 */
function drawTextNote(
  ctx: CanvasRenderingContext2D,
  note: Extract<Annotation, { type: "text" }>,
  toPx: (x: number, y: number) => { x: number; y: number },
  pxPerWorldY: number,
): void {
  const { x, y } = toPx(note.x, note.y);
  const fontPx = Math.max(6, note.sizeWorld * pxPerWorldY);
  ctx.font = `${fontPx}px sans-serif`;
  ctx.textBaseline = "top";

  const paddingX = fontPx * 0.4;
  const paddingY = fontPx * 0.3;
  const boxWidth = ctx.measureText(note.text).width + paddingX * 2;
  const boxHeight = fontPx * 1.3 + paddingY * 2;

  ctx.fillStyle = note.color;
  ctx.fillRect(x, y, boxWidth, boxHeight);
  ctx.fillStyle = readableTextColor(note.color);
  ctx.fillText(note.text, x + paddingX, y + paddingY);
}

/** Black or white, whichever reads better against `backgroundColor` (relative luminance). */
function readableTextColor(backgroundColor: string): string {
  const rgb = parseHexColor(backgroundColor);
  if (!rgb) return "#000000";
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.6 ? "#000000" : "#ffffff";
}

function parseHexColor(color: string): { r: number; g: number; b: number } | null {
  const match = /^#([0-9a-f]{6})$/i.exec(color.trim());
  if (!match) return null;
  const value = parseInt(match[1], 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}
